import { useState } from 'react'

import _ from 'lodash'
import { useCurrentStatusStore } from 'store/currentStatusStore'
import { getAPI } from 'utils/getAPI'

import type {
  GetMstCenterStockItemCenterStockItemGetRequest,
  ResponseResult,
  ResponseUpdate,
  MasterCenterStockItemUpdateMstCenterStockItemUpdatePostRequest,
  CenterStockItem,
  MstCenterStockItem,
  AlertCenterStockItemData,
  AlertMstCenterStockItem,
  GetAlertCenterStockItemCenterStockItemAlertGetRequest,
} from 'api-client'
import type { ErrorResponse } from 'types/types'

export interface MstCenterStockItemSeq extends MstCenterStockItem{
  id: string
}

export interface MstCenterStockItemsSeq extends CenterStockItem{
  list: MstCenterStockItemSeq[]
}

export interface AlertMstCenterStockItemSeq extends AlertMstCenterStockItem {
  id: string
}

export interface AlertCenterStockItemDataSeq extends AlertCenterStockItemData {
  list: AlertMstCenterStockItemSeq[]
}

export const useCenterStockItem = () => {
  const [centerstockitemSearch, setCenterStockItemSearch] = useState<CenterStockItem>({
    datacount: 0,
    maxcount: 0,
    lastUpdateDatetime: null,
    list: [],
  })
  const [centerstockitemSearchIsLoading, setCenterStockItemSearchIsLoading] = useState(false)
  const [centerstockitemUpdateIsLoading, setCenterStockItemUpdateIsLoading] = useState(false)
  const [centerstockitemSearchResult, setCenterStockItemSearchResult] = useState<ResponseResult | undefined>(undefined)
  const [updateResult, setUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const [alertCenterStockItems, setAlertCenterStockItems] = useState<AlertCenterStockItemDataSeq>({
    datacount: 0,
    maxcount: 0,
    lastUpdateDatetime: null,
    list: [],
  })
  const [isAlertCenterStockItemsLoading, setIsAlertCenterStockItemsLoading] = useState(false)
  const [alertCenterStockItemsResult, setAlertCenterStockItemsResult] = useState<ResponseResult | undefined>(undefined)
  const { setCurrentStatuses } = useCurrentStatusStore()
  const [result, setResult] = useState<ResponseResult>()
  const [isUpdating, setIsUpdating] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const fetchCenterStockItemSearch = async (searchParam: GetMstCenterStockItemCenterStockItemGetRequest) => {
    setCenterStockItemSearchIsLoading(true)
    setCenterStockItemSearchResult(undefined)
    setIsLoading(true)
    setResult(undefined)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.getMstCenterStockItemCenterStockItemGet(searchParam)

      setCenterStockItemSearch({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      setCenterStockItemSearchResult(response.result)
      setResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setCenterStockItemSearchResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `センター在庫管理マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setCenterStockItemSearchIsLoading(false)
      setIsLoading(false)
    }
  }


  const fetchAlertCenterStockItems = async (searchParam: GetAlertCenterStockItemCenterStockItemAlertGetRequest) => {
    setAlertCenterStockItemsResult(undefined)
    setIsAlertCenterStockItemsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.getAlertCenterStockItemCenterStockItemAlertGet(searchParam)

      setAlertCenterStockItems({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        })),
      })
      setAlertCenterStockItemsResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setAlertCenterStockItemsResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `センター在庫管理マスタアラートの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setIsAlertCenterStockItemsLoading(false)
    }
  }

  const fetchCenterStockItemUpdate = async (searchParam: MasterCenterStockItemUpdateMstCenterStockItemUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true)
    setCenterStockItemSearchResult(undefined)
    setResult(undefined)
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.masterCenterStockItemUpdateMstCenterStockItemUpdatePost(searchParam);
      setResult(response.result)
      setUpdateResult(response);

      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || '入力エラーがあります');
        Object.assign(error, {
            ...response
        });
        throw error;
      }
      return response;
    } catch (error) {
      if (error && typeof error === 'object' && 'result' in error) {
          const apiResponse = error as ResponseUpdate; 
          await new Promise<void>(resolve => {
            setUpdateResult(apiResponse);
            resolve();
          });
          return await Promise.reject(apiResponse);
      }
      const { response } = error as any;
      const errorResponse = {
          errors: [],
          result: {
              status: 'error',
              message: response && response.status === 422 ? '入力値に誤りがあります。' : `センター在庫管理マスタの更新でエラーが発生しました。`,
              systemDate: null,
              pageNo: 0,
              systemInfo: null,
          },
          updateCount: null,
          lastUpdateDatetime: null,
      };
      await new Promise<void>(resolve => {
        setUpdateResult(errorResponse);
          setResult(errorResponse.result)
          resolve();
      });
      return await Promise.reject(errorResponse);      
      // const { response } = error as any;
      // const errorResult: ResponseUpdate = {
      //     errors: [],
      //     result: {
      //         status: 'error',
      //         message: response && response.status === 422 ? '入力値に誤りがあります。' : `エラーが発生しました。`,
      //         systemDate: null,
      //         pageNo: 0,
      //         systemInfo: null,
      //     },
      //     updateCount: null,
      // };
      // setUpdateResult(errorResult);
      // setResult(errorResult.result)
      // throw error; 
    } finally {
      setCenterStockItemUpdateIsLoading(false);
      setIsUpdating(false);
    }
  };

  return {
  	result,
  	isLoading,
  	isUpdating,
    centerstockitemSearch,
    fetchCenterStockItemSearch,
    centerstockitemSearchResult,
    centerstockitemSearchIsLoading,

    fetchCenterStockItemUpdate,
    updateResult,
    centerstockitemUpdateIsLoading,

    alertCenterStockItems,
    fetchAlertCenterStockItems,
    alertCenterStockItemsResult,
    isAlertCenterStockItemsLoading,
  }
}
